import { scrubUrl } from '../sentry/scrubbers';

export interface FrontendTelemetryEnv {
  endpoint?: string;
  environment?: string;
  release?: string;
  enabled?: string;
}

export interface FrontendTelemetryConfig {
  app: string;
  surface: string;
  serviceName: string;
  endpoint: string;
  environment: string;
  release: string;
  enabled: boolean;
}

interface OtlpAnyValue {
  stringValue?: string;
  intValue?: string;
  doubleValue?: number;
  boolValue?: boolean;
}

interface OtlpLogRecord {
  attributes: Array<{ key: string; value: OtlpAnyValue }>;
  body?: OtlpAnyValue;
  severityNumber: number;
  severityText: string;
  timeUnixNano: string;
}

interface FrontendTelemetryEvent {
  eventName: string;
  level?: 'debug' | 'info' | 'warning' | 'error';
  message?: string;
  attributes?: Record<string, string | number | boolean | undefined>;
}

type MinimalEventTarget = {
  addEventListener?: (
    type: string,
    listener: (...args: unknown[]) => void,
  ) => void;
};

type MinimalNavigator = {
  userAgent?: string;
};

type MinimalLocation = {
  href?: string;
  pathname?: string;
};

type MinimalHistory = {
  pushState?: (...args: unknown[]) => void;
  replaceState?: (...args: unknown[]) => void;
};

type MinimalPerformance = {
  now?: () => number;
  getEntriesByType?: (type: string) => unknown[];
};

type FrontendGlobalScope = typeof globalThis & {
  document?: {
    title?: string;
    readyState?: string;
  } & MinimalEventTarget;
  history?: MinimalHistory;
  location?: MinimalLocation;
  navigator?: MinimalNavigator;
  performance?: MinimalPerformance;
};

const globalScope = globalThis as FrontendGlobalScope;
const LOGS_SCOPE_NAME = '@tov-plus/observability/browser';
const DEFAULT_FLUSH_INTERVAL_MS = 5_000;
const DEFAULT_BATCH_SIZE = 10;

function normalizeEndpoint(endpoint: string) {
  const trimmed = endpoint.trim();

  if (!trimmed) {
    return '';
  }

  return trimmed.endsWith('/v1/logs')
    ? trimmed
    : `${trimmed.replace(/\/+$/u, '')}/v1/logs`;
}

function enabledFromEnv(value: string | undefined) {
  if (value === undefined) {
    return true;
  }

  return value !== 'false' && value !== '0';
}

export function parseFrontendTelemetryConfig(
  env: FrontendTelemetryEnv,
  input: Pick<FrontendTelemetryConfig, 'app' | 'surface' | 'serviceName'>,
): FrontendTelemetryConfig {
  const endpoint = normalizeEndpoint(env.endpoint ?? '');

  return {
    ...input,
    endpoint,
    environment: env.environment?.trim() || 'development',
    release: env.release?.trim() || '0.0.0',
    enabled: Boolean(endpoint) && enabledFromEnv(env.enabled),
  };
}

function toAnyValue(
  value: string | number | boolean | undefined,
): OtlpAnyValue | null {
  if (typeof value === 'string') {
    return { stringValue: value };
  }

  if (typeof value === 'boolean') {
    return { boolValue: value };
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return Number.isInteger(value)
      ? { intValue: String(Math.trunc(value)) }
      : { doubleValue: value };
  }

  return null;
}

function severityNumberForLevel(level: string | undefined) {
  switch (level) {
    case 'error':
      return 17;
    case 'warning':
      return 13;
    case 'debug':
      return 5;
    case 'info':
    default:
      return 9;
  }
}

function nowUnixNano() {
  return String(BigInt(Date.now()) * 1_000_000n);
}

function scrubCurrentUrl() {
  const href = globalScope.location?.href;
  return typeof href === 'string' ? (scrubUrl(href) ?? href) : undefined;
}

function currentPath() {
  const pathname = globalScope.location?.pathname;
  return typeof pathname === 'string' && pathname.length > 0 ? pathname : '/';
}

function buildResourceAttributes(config: FrontendTelemetryConfig) {
  return [
    { key: 'service_name', value: { stringValue: config.serviceName } },
    {
      key: 'deployment.environment',
      value: { stringValue: config.environment },
    },
    { key: 'environment', value: { stringValue: config.environment } },
    { key: 'service.version', value: { stringValue: config.release } },
    { key: 'project_name', value: { stringValue: 'tovplus' } },
    { key: 'runtime', value: { stringValue: 'browser' } },
    { key: 'surface', value: { stringValue: config.surface } },
    { key: 'app', value: { stringValue: config.app } },
  ];
}

export function buildFrontendLogRecord(
  config: FrontendTelemetryConfig,
  event: FrontendTelemetryEvent,
): OtlpLogRecord {
  const level = event.level ?? 'info';
  const attributes = Object.entries({
    app: config.app,
    environment: config.environment,
    event_name: event.eventName,
    level,
    page_path: currentPath(),
    page_url: scrubCurrentUrl(),
    project_name: 'tovplus',
    release: config.release,
    runtime: 'browser',
    service_name: config.serviceName,
    surface: config.surface,
    user_agent: globalScope.navigator?.userAgent,
    'loki.attribute.labels':
      'level,service_name,environment,project_name,surface,event_name',
    ...event.attributes,
  })
    .map(([key, value]) => {
      const anyValue = toAnyValue(value);
      if (!anyValue) {
        return null;
      }

      return { key, value: anyValue };
    })
    .filter(
      (value): value is { key: string; value: OtlpAnyValue } => value !== null,
    );

  return {
    attributes,
    body: {
      stringValue:
        event.message ?? `browser telemetry event: ${event.eventName}`,
    },
    severityNumber: severityNumberForLevel(level),
    severityText: level.toUpperCase(),
    timeUnixNano: nowUnixNano(),
  };
}

function buildLogsPayload(
  config: FrontendTelemetryConfig,
  records: OtlpLogRecord[],
) {
  return {
    resourceLogs: [
      {
        resource: {
          attributes: buildResourceAttributes(config),
        },
        scopeLogs: [
          {
            scope: {
              name: LOGS_SCOPE_NAME,
            },
            logRecords: records,
          },
        ],
      },
    ],
  };
}

class FrontendTelemetryClient {
  private readonly queue: OtlpLogRecord[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly fetchImpl =
    typeof globalScope.fetch === 'function'
      ? globalScope.fetch.bind(globalScope)
      : null;

  constructor(private readonly config: FrontendTelemetryConfig) {}

  track(event: FrontendTelemetryEvent) {
    this.queue.push(buildFrontendLogRecord(this.config, event));

    if (this.queue.length >= DEFAULT_BATCH_SIZE) {
      void this.flush();
      return;
    }

    if (this.flushTimer === null) {
      this.flushTimer = setTimeout(() => {
        void this.flush();
      }, DEFAULT_FLUSH_INTERVAL_MS);
    }
  }

  async flush() {
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    if (this.queue.length === 0 || !this.fetchImpl) {
      return;
    }

    const records = this.queue.splice(0, this.queue.length);

    try {
      await this.fetchImpl(this.config.endpoint, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
        body: JSON.stringify(buildLogsPayload(this.config, records)),
        keepalive: true,
      });
    } catch {
      // Browser telemetry must stay non-blocking.
    }
  }
}

function performanceNow() {
  return typeof globalScope.performance?.now === 'function'
    ? globalScope.performance.now()
    : 0;
}

function registerUnhandledErrorTelemetry(client: FrontendTelemetryClient) {
  const eventTarget = globalScope as MinimalEventTarget;
  if (typeof eventTarget.addEventListener !== 'function') {
    return;
  }

  eventTarget.addEventListener('error', (...args) => {
    const event = args[0] as
      | {
          message?: string;
          error?: { message?: string; name?: string };
          filename?: string;
        }
      | undefined;

    client.track({
      eventName: 'unhandled_error',
      level: 'error',
      message:
        event?.error?.message ?? event?.message ?? 'Unhandled browser error.',
      attributes: {
        error_name: event?.error?.name,
        source_file: event?.filename,
      },
    });
  });

  eventTarget.addEventListener('unhandledrejection', (...args) => {
    const event = args[0] as { reason?: unknown } | undefined;
    const reason = event?.reason;
    const message =
      reason instanceof Error
        ? reason.message
        : typeof reason === 'string'
          ? reason
          : 'Unhandled promise rejection.';

    client.track({
      eventName: 'unhandled_rejection',
      level: 'error',
      message,
      attributes: {
        error_name: reason instanceof Error ? reason.name : undefined,
      },
    });
  });
}

function registerNavigationTimingTelemetry(client: FrontendTelemetryClient) {
  const timing = globalScope.performance?.getEntriesByType?.(
    'navigation',
  )?.[0] as
    | {
        duration?: number;
        domContentLoadedEventEnd?: number;
        loadEventEnd?: number;
        type?: string;
      }
    | undefined;

  if (!timing) {
    return;
  }

  client.track({
    eventName: 'navigation_timing',
    level: 'info',
    message: 'Navigation timing recorded.',
    attributes: {
      duration_ms:
        typeof timing.duration === 'number'
          ? Number(timing.duration.toFixed(3))
          : undefined,
      dom_content_loaded_ms:
        typeof timing.domContentLoadedEventEnd === 'number'
          ? Number(timing.domContentLoadedEventEnd.toFixed(3))
          : undefined,
      load_event_end_ms:
        typeof timing.loadEventEnd === 'number'
          ? Number(timing.loadEventEnd.toFixed(3))
          : undefined,
      navigation_type: timing.type,
    },
  });
}

function registerPageViewTelemetry(client: FrontendTelemetryClient) {
  let lastPath = '';

  const emitPageView = (source: string) => {
    const path = currentPath();
    if (!path || path === lastPath) {
      return;
    }

    lastPath = path;
    client.track({
      eventName: 'page_view',
      level: 'info',
      message: `Page view: ${path}`,
      attributes: {
        document_title: globalScope.document?.title,
        navigation_source: source,
      },
    });
  };

  emitPageView('initial_load');

  const history = globalScope.history;
  const eventTarget = globalScope as MinimalEventTarget;

  if (
    history &&
    typeof history.pushState === 'function' &&
    typeof history.replaceState === 'function'
  ) {
    const originalPushState = history.pushState.bind(history);
    const originalReplaceState = history.replaceState.bind(history);

    history.pushState = (...args) => {
      originalPushState(...args);
      emitPageView('history_push_state');
    };

    history.replaceState = (...args) => {
      originalReplaceState(...args);
      emitPageView('history_replace_state');
    };
  }

  if (typeof eventTarget.addEventListener === 'function') {
    eventTarget.addEventListener('popstate', () => emitPageView('popstate'));
    eventTarget.addEventListener('hashchange', () =>
      emitPageView('hashchange'),
    );
  }
}

export function initFrontendBrowserTelemetry(config: FrontendTelemetryConfig) {
  if (!config.enabled) {
    return null;
  }

  const client = new FrontendTelemetryClient(config);
  const bootstrapStartedAt = performanceNow();

  client.track({
    eventName: 'telemetry_started',
    level: 'info',
    message: 'Browser telemetry initialized.',
  });

  registerPageViewTelemetry(client);
  registerUnhandledErrorTelemetry(client);

  const eventTarget = globalScope as MinimalEventTarget;
  if (typeof eventTarget.addEventListener === 'function') {
    eventTarget.addEventListener('load', () => {
      client.track({
        eventName: 'bootstrap_complete',
        level: 'info',
        message: 'Browser bootstrap completed.',
        attributes: {
          bootstrap_duration_ms: Number(
            (performanceNow() - bootstrapStartedAt).toFixed(3),
          ),
          document_ready_state: globalScope.document?.readyState,
        },
      });
      registerNavigationTimingTelemetry(client);
      void client.flush();
    });

    eventTarget.addEventListener('pagehide', () => {
      void client.flush();
    });
  } else {
    registerNavigationTimingTelemetry(client);
  }

  return client;
}
